/* eslint react/jsx-no-target-blank: ["warn", {"allowReferrer": true}] */
// Keep the origin referrer for Bunny's allowed-domain checks.
import React, {memo, useEffect, useId, useRef, useState} from 'react';
import {connectBunnyPlayer,playbackPosition,parsePlaybackTime,formatPlaybackTime} from './bunnyPlayback';
import BunnyMp4Player from './BunnyMp4Player';
import {bunnyMp4Sources} from './bunnyVideoSources';
import './BunnyVideoPlayer.css';

export function bunnyVideoUrl(libraryId,videoId) {
    const library=String(libraryId ?? '').trim(),video=String(videoId ?? '').trim();
    if (!library || !video) return '';
    // Prebuffer only after the explanation is revealed. Cap unnecessary HD
    // downloads in a small panel; Bunny still adapts quality and fullscreen size.
    // https://bunny.net/docs/stream/embedding
    return `https://player.mediadelivery.net/embed/${encodeURIComponent(library)}/${encodeURIComponent(video)}?autoplay=false&loop=false&muted=false&preload=true&playsinline=true&nativeHls=auto&rememberPosition=true&levelCap=true`;
}
export function resumeVideoUrl(src,seconds,autoplay=false) {
    const url=new URL(src);
    if(Number.isFinite(seconds) && seconds>0)url.searchParams.set('t',String(Math.max(0,Math.floor(seconds)-1)));
    url.searchParams.set('autoplay',String(autoplay));
    return url.toString();
}
function VideoFrame({src,title,initialSeconds=0,initialAutoplay=false}) {
    const frame=useRef(null),position=useRef(initialSeconds),automaticRetries=useRef(0),recover=useRef(null);
    const [attempt,setAttempt]=useState(()=>({revision:0,src:initialSeconds>0 || initialAutoplay?resumeVideoUrl(src,initialSeconds,initialAutoplay):src,resumeAt:initialSeconds}));
    const [status,setStatus]=useState('loading');
    const [offline,setOffline]=useState(()=>navigator.onLine===false);
    const [lastPosition,setLastPosition]=useState(initialSeconds);
    const [duration,setDuration]=useState(0),[jumpTime,setJumpTime]=useState(''),[jumpError,setJumpError]=useState('');
    const jumpId=useId();
    const reload=(automatic=false)=>{
        const seconds=position.current;
        setLastPosition(seconds);setStatus(automatic?'recovering':'loading');
        setAttempt(previous=>({revision:previous.revision+1,src:resumeVideoUrl(src,seconds,automatic),resumeAt:seconds}));
    };
    const jump=event=>{
        event.preventDefault();
        const seconds=parsePlaybackTime(jumpTime);
        if(seconds===null || (duration>0 && seconds>=duration)){setJumpError(duration>0?`Enter a time before ${formatPlaybackTime(duration)}, such as 1:30.`:'Enter a time such as 1:30 or 0:00.');return;}
        setJumpError('');position.current=seconds;setLastPosition(seconds);automaticRetries.current=0;
        const next=new URL(resumeVideoUrl(src,seconds,true));next.searchParams.set('t',String(seconds));
        setStatus('recovering');setAttempt(previous=>({revision:previous.revision+1,src:next.toString(),resumeAt:seconds}));
    };
    recover.current=reload;
    useEffect(()=>{
        let ready=false,playing=false,ended=false,hasPlayback=false,lastMovement=Date.now(),startedAt=Date.now(),lastSeen=null,lastObservation=Date.now();
        let seeking=false,seekTarget=null,healthyProgress=0,awaitingResume=attempt.resumeAt>0,playbackError=null;
        const online=()=>navigator.onLine!==false;
        const show=value=>{setStatus(value);};
        const setPlaying=value=>{
            // Polls confirm intent; an unchanged response is not playback progress.
            if(value!==playing){playing=value;lastMovement=Date.now();}
            if(value){playbackError=null;hasPlayback=true;}
            if(!value && !playbackError)show(ended?'ended':hasPlayback?'paused':'ready');
        };
        const updateTime=value=>{
            const seconds=playbackPosition(value);
            if(seconds===null || ended)return;
            // The newly loaded player may report zero before its start offset.
            if(awaitingResume && seconds<Math.max(0,attempt.resumeAt-2))return;
            awaitingResume=false;
            const now=Date.now(),delta=lastSeen===null?0:seconds-lastSeen,elapsed=(now-lastObservation)/1000;
            const jumped=lastSeen!==null && (delta<-.75 || delta>Math.max(4,elapsed*4+2));
            if(jumped){seeking=true;seekTarget=seconds;healthyProgress=0;lastMovement=now;if(playing)show('seeking');}
            else if(lastSeen===null || delta>.05){
                lastMovement=now;
                if(delta>0 && !seeking)healthyProgress+=delta;
                if(seeking && seekTarget!==null && seconds>seekTarget+.1){seeking=false;seekTarget=null;healthyProgress=0;}
                if(playing && !seeking)show('playing');
                // A later stall can recover after genuine sustained playback.
                // A seek jump or repeated timestamp never replenishes retries.
                if(healthyProgress>=10)automaticRetries.current=0;
            }
            lastSeen=seconds;lastObservation=now;
            if(seconds>0 || hasPlayback){position.current=seconds;setLastPosition(previous=>Math.floor(previous)===Math.floor(seconds)?previous:seconds);}
        };
        const disconnect=connectBunnyPlayer(frame.current,(event,value)=>{
            if(event==='ready'){ready=true;startedAt=Date.now();show('ready');}
            if(event==='play'){setPlaying(true);ended=false;hasPlayback=true;lastMovement=Date.now();show('playing');}
            if(event==='pause')setPlaying(false);
            if(event==='getPaused' && typeof value==='boolean')setPlaying(!value);
            if(event==='getDuration'){const total=playbackPosition(value);if(total!==null)setDuration(total);}
            if(event==='ended'){playing=false;ended=true;seeking=false;position.current=0;setLastPosition(0);show('ended');}
            if(event==='timeupdate' || event==='getCurrentTime')updateTime(value);
            if(event==='timeupdate' && Number.isFinite(value?.duration) && value.duration>0)setDuration(value.duration);
            if(event==='seeked'){
                ended=false;hasPlayback=true;awaitingResume=false;
                updateTime(value);disconnect.requestState();
            }
            if(event==='error'){
                if(Number(value?.code)===2)return;
                playing=false;
                playbackError=Number(value?.code)===5?'tap-play':'error';show(playbackError);
            }
        });
        const connectivity=()=>{
            setOffline(!online());lastMovement=Date.now();startedAt=Date.now();
            if(online())show(playing?'buffering':ready?'ready':'loading');
        };
        const visibility=()=>{lastMovement=Date.now();startedAt=Date.now();lastObservation=Date.now();if(!document.hidden)disconnect.requestState();};
        window.addEventListener('online',connectivity);window.addEventListener('offline',connectivity);
        document.addEventListener('visibilitychange',visibility);
        const poll=setInterval(()=>{if(!document.hidden && online())disconnect.requestState();},2000);
        const timer=setInterval(()=>{
            if(document.hidden || !online())return;
            if(!ready && Date.now()-startedAt>=15000){show('slow');return;}
            if(!playing || ended)return;
            const stalledFor=Date.now()-lastMovement;
            if(stalledFor>=(seeking?15000:25000) && automaticRetries.current<1){
                automaticRetries.current+=1;playing=false;
                recover.current(true);
            } else if(stalledFor>=12000)show(seeking?'seek-stalled':'buffering');
        },1000);
        return ()=>{disconnect();clearInterval(timer);clearInterval(poll);window.removeEventListener('online',connectivity);window.removeEventListener('offline',connectivity);document.removeEventListener('visibilitychange',visibility);};
    },[attempt]);
    const messages={
        loading:'Loading video player…',ready:'Ready when you are. Press Play to watch.',playing:'Playing your tutor’s explanation.',paused:'Paused. Press Play to continue.',ended:'Explanation complete. You can replay it anytime.',
        recovering:'Reconnecting at the selected time…',
        seeking:'Loading the point you skipped to…',
        'seek-stalled':'This part is taking longer to load. Try Reload video or Jump & play below.',
        buffering:'Playback is taking longer to buffer. In the video settings, try Auto quality or a lower resolution such as 360p.',
        slow:'The player is taking longer than expected. Try Reload video or open it separately.',
        error:'The video could not play. Try Reload video. If this continues, tell your tutor which question is affected.',
        'tap-play':'Press Play in the video to continue. Your browser needs a tap before it can resume.'
    };
    const trouble=['buffering','seek-stalled','slow','error','recovering'].includes(status);
    return <section className={`instil-video${trouble?' instil-video-trouble':''}`} aria-label={title}>
        <div className="instil-video-heading"><span aria-hidden="true">▷</span><strong>{title}</strong><span>Watch &amp; learn</span></div>
        <div className="instil-video-stage">
            <iframe ref={frame} key={attempt.revision} src={attempt.src} title={title} loading="eager"
                allow="autoplay; encrypted-media; picture-in-picture; fullscreen" allowFullScreen
                referrerPolicy="strict-origin-when-cross-origin" onError={()=>setStatus('error')}/>
        </div>
        <div className="instil-video-help">
            <p role="status">{offline?'You’re offline. Reconnect to the internet, then press Play to continue.':messages[status]}</p>
            <div><button type="button" onClick={()=>reload()} disabled={offline}>Reload video</button><a href={resumeVideoUrl(src,lastPosition).replace('/embed/','/play/')} target="_blank" rel="noopener" referrerPolicy="strict-origin-when-cross-origin">Open video separately ↗</a></div>
            <form className="instil-video-jump" onSubmit={jump}>
                <label htmlFor={jumpId}>Jump to a time{duration>0 && <span> · {formatPlaybackTime(duration)} total</span>}</label>
                <div><input id={jumpId} type="text" value={jumpTime} onChange={e=>{setJumpTime(e.target.value);setJumpError('');}} placeholder="e.g. 2:26" maxLength={12} aria-describedby={jumpError?`${jumpId}-error`:undefined} aria-invalid={!!jumpError}/><button type="submit" disabled={offline}>Jump &amp; play</button></div>
                {jumpError && <p id={`${jumpId}-error`} role="alert">{jumpError}</p>}
            </form>
            <small>Video freezing? Choose Auto or a lower quality in the video’s settings. Reload returns to your latest position. Jump & play opens the selected time in a fresh player if the video’s seek bar gets stuck.</small>
        </div>
    </section>;
}
function StandardVideo({src,sources,title}) {
    const [fallback,setFallback]=useState(null);
    return fallback ? <><p className="instil-video-mode-notice">Using the streaming player. <button type="button" onClick={()=>setFallback(null)}>Restart with standard playback</button></p><VideoFrame src={src} title={title} initialSeconds={fallback.seconds} initialAutoplay={fallback.autoplay}/></> : <BunnyMp4Player sources={sources} title={title} onFallback={setFallback}/>;
}
const BunnyVideoPlayer = memo(function BunnyVideoPlayer({libraryId,videoId,title='Explanation Video',preferStandard=false}) {
    const src=bunnyVideoUrl(libraryId,videoId);
    const sources=preferStandard?bunnyMp4Sources(libraryId,videoId):null;
    return src ? sources?<StandardVideo key={src} src={src} sources={sources} title={title}/>:<VideoFrame key={src} src={src} title={title}/> : null;
});
export default BunnyVideoPlayer;
